import { Pipe, PipeTransform, inject } from '@angular/core';
import { DeviceSoftwareStatus } from '@nimmsta/ng-commons-lib/models';
import { parseEnumValue } from '@nimmsta/ng-commons-lib/utils/enum';
import { TranslateService } from '@ngx-translate/core';
import { MaintenanceStatus } from '../../models/scanner';

interface ScannerListStatusValue {
  maintenanceStatus?: MaintenanceStatus | null;
  softwareStatus?: DeviceSoftwareStatus | null;
}

@Pipe({ name: 'toScannerListStatusBadges' })
export class ScannerListStatusPipe implements PipeTransform {
  private translate = inject(TranslateService);

  transform(serializedStatuses?: string): string {
    if (!serializedStatuses) {
      return '-';
    }

    const statuses = this.parseStatuses(serializedStatuses);
    if (!statuses) {
      return '-';
    }

    const badges = [
      this.buildMaintenanceBadge(statuses.maintenanceStatus),
      this.buildSoftwareBadge(statuses.softwareStatus),
    ].filter((badge): badge is string => Boolean(badge));

    if (badges.length === 0) {
      return '-';
    }

    return `<span class="status-badge-group">${badges.join('')}</span>`;
  }

  private parseStatuses(
    serializedStatuses: string,
  ): ScannerListStatusValue | null {
    try {
      const parsed = JSON.parse(serializedStatuses) as {
        maintenanceStatus?: unknown;
        softwareStatus?: unknown;
      };
      return {
        maintenanceStatus: parseEnumValue(
          MaintenanceStatus,
          parsed.maintenanceStatus,
        ),
        softwareStatus: parseEnumValue(
          DeviceSoftwareStatus,
          parsed.softwareStatus,
        ),
      };
    } catch {
      return null;
    }
  }

  private buildMaintenanceBadge(
    maintenanceStatus?: MaintenanceStatus | null,
  ): string | null {
    if (!maintenanceStatus) {
      return null;
    }

    const statusKey = this.statusKeyFromMaintenanceStatus(maintenanceStatus);
    const text = this.translate.instant(
      `scanner.maintenance.statusValues.${statusKey}`,
    );
    return this.renderBadge(
      text,
      maintenanceStatus === MaintenanceStatus.OK ? 'active' : 'inactive',
    );
  }

  private buildSoftwareBadge(
    softwareStatus?: DeviceSoftwareStatus | null,
  ): string | null {
    if (!softwareStatus) {
      return null;
    }

    const statusKey = this.statusKeyFromSoftwareStatus(softwareStatus);
    const text = this.translate.instant(
      `scanner.maintenance.softwareStatusValues.${statusKey}`,
    );
    return this.renderBadge(
      text,
      this.softwareBadgeClassFromStatus(softwareStatus),
    );
  }

  private renderBadge(text: string, badgeClass: string): string {
    return `<span class="badge rounded-pill text-bg-light"><span class="dot ${badgeClass}"></span>${text}</span>`;
  }

  private softwareBadgeClassFromStatus(status: DeviceSoftwareStatus): string {
    if (status === DeviceSoftwareStatus.OK) {
      return 'active';
    }

    if (
      status === DeviceSoftwareStatus.WARNING ||
      status === DeviceSoftwareStatus.UPDATE_REQUESTED ||
      status === DeviceSoftwareStatus.IMAGER_FIRMWARE_UPDATE_REQUIRED
    ) {
      return 'needs-attention';
    }

    return 'inactive';
  }

  private statusKeyFromMaintenanceStatus(status: MaintenanceStatus): string {
    switch (status) {
      case MaintenanceStatus.OK:
        return 'ok';
      case MaintenanceStatus.MISSING_DATA:
        return 'missing_data';
      case MaintenanceStatus.BATTERY_WARNING:
        return 'battery_warning';
      case MaintenanceStatus.SCAN_ENGINE_WARNING:
        return 'scan_engine_warning';
      case MaintenanceStatus.SOFTWARE_WARNING:
        return 'software_warning';
      case MaintenanceStatus.CUSTOM_FIRMWARE:
        return 'custom_firmware';
      case MaintenanceStatus.MULTIPLE_WARNINGS:
        return 'multiple_warnings';
    }
    const exhaustiveCheck: never = status;
    return exhaustiveCheck;
  }

  private statusKeyFromSoftwareStatus(status: DeviceSoftwareStatus): string {
    switch (status) {
      case DeviceSoftwareStatus.OK:
        return 'ok';
      case DeviceSoftwareStatus.WARNING:
        return 'warning';
      case DeviceSoftwareStatus.UPDATE_REQUESTED:
        return 'update_requested';
      case DeviceSoftwareStatus.IMAGER_FIRMWARE_UPDATE_REQUIRED:
        return 'imager_firmware_update_required';
      case DeviceSoftwareStatus.CUSTOM_FIRMWARE:
        return 'custom_firmware';
      case DeviceSoftwareStatus.MISSING_DATA:
        return 'missing_data';
    }
    const exhaustiveCheck: never = status;
    return exhaustiveCheck;
  }
}
